<template>
  <ListComponent title="Draft Sub-hires" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="12" md="4" class="pl-0 pt-0 pb-2 d-flex align-center" style="gap:8px">
          <v-text-field
            label="Search (Draft Sub-hire No / Container No)"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            style="min-width:150px"
            v-model="search"
            @click:clear="onSearchClear"
            @keyup.enter="onSearch"
          />
        </v-col>
        <v-col cols="6" md="2" class="pt-0 pb-2">
          <MultiSelect
            label="Status"
            :items="statusList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.status"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="2" class="pt-0 pb-2">
          <DateField
            :inputProps="{
              dense: true,
              label: 'Movement Date From',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="filters.movement_date_from"
            @input="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="2" class="pt-0 pb-2">
          <DateField
            :inputProps="{
              dense: true,
              label: 'Movement Date To',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="filters.movement_date_to"
            @input="onFilterChange"
          />
        </v-col>
        <v-col cols="12" md="2" class="pr-0 pt-0 pb-2 d-flex align-center justify-end">
          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 190px)"
            class="ag-theme-alpine"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :rowData="allList"
          />
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <DraftSubHireLogsDialog v-model="showLogsDialog" :draftSubHire="selectedDraftSubHire" />
    </template>
  </ListComponent>
</template>
<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import Pagination from "@/components/General/Pagination.vue";
import DraftDAStatusChip from "@/components/AgGridComponents/DraftDAStatusChip.vue";
import DraftSubHireLogsButton from "@/components/AgGridComponents/DraftSubHireLogsButton.vue";
import DraftSubHireLogsDialog from "@/components/ModuleBased/dialogs/DraftSubHireLogsDialog.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    agColumnHeader: CustomHeader,
    AgGridVue,
    Pagination,
    DraftDAStatusChip,
    DraftSubHireLogsButton,
    DraftSubHireLogsDialog,
    MultiSelect,
    DateField,
  },
  data() {
    return {
      allList: [],
      totalItems: 0,
      search: "",
      filters: {
        status: null,
        movement_date_from: null,
        movement_date_to: null,
      },
      statusList: [
        { text: "Open", value: "open" },
        { text: "Confirmed", value: "confirmed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      pageNo: 1,
      itemsPerPage: 50,
      permissions: null,
      showLogsDialog: false,
      selectedDraftSubHire: null,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.gridOptions.api.sizeColumnsToFit(),
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "Draft Sub-hire No",
          field: "dsh_no",
          sortable: true,
          minWidth: 170,
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 120,
          cellRendererFramework: "DraftDAStatusChip",
        },
        {
          headerName: "Container No",
          field: "container_number",
          sortable: true,
          minWidth: 160,
          cellClass: (params) =>
            params.data && params.data.emergency_order_container_id
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openContainerDetails(params.data),
        },
        {
          headerName: "VBS Ref No",
          field: "vbs_reference_no",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Voyage ID",
          field: "voyage_id",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Container Type",
          field: "container_type_name",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Lane",
          field: "lane_title",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Origin",
          field: "origin_name",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Destination",
          field: "destination_name",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Driver ID",
          field: "driver_id_raw",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Vehicle No",
          field: "vehicle_no",
          sortable: true,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Movement Date",
          field: "movement_date",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value) : "-",
        },
        {
          headerName: "DA No",
          field: "da_no",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
          cellClass: (params) =>
            params.data && params.data.da
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openDaDetails(params.data),
        },
        {
          headerName: "Leg Status",
          field: "leg_status",
          sortable: true,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Sub-Hire ID",
          field: "sub_hire_id",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Confirmed On",
          field: "confirmed_on",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Created",
          field: "created",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Logs",
          field: "logs",
          cellRendererFramework: "DraftSubHireLogsButton",
          pinned: "right",
          minWidth: 80,
          width: 80,
          suppressSizeToFit: true,
          sortable: false,
        },
      ],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  methods: {
    openContainerDetails(row) {
      if (row && row.emergency_order_container_id) {
        this.$router.push({
          path: `/app/admin/emergency-order-container/details/${row.emergency_order_container_id}`,
          query: {},
        });
      }
    },
    openDaDetails(row) {
      if (row && row.da) {
        this.$router.push({
          path: `/app/admin/delivery-advice/details/${row.da}`,
          query: {},
        });
      }
    },
    openDraftSubHireLogs(row) {
      this.selectedDraftSubHire = row;
      this.showLogsDialog = true;
    },
    onSearch() {
      this.pageNo = 1;
      this.getList();
    },
    onSearchClear() {
      this.search = "";
      this.pageNo = 1;
      this.getList();
    },
    onFilterChange() {
      this.pageNo = 1;
      this.getList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getList();
    },
    prevPage() {
      this.pageNo--;
      this.getList();
    },
    nextPage() {
      this.pageNo++;
      this.getList();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    getList() {
      this.$bus.$emit("showLoader", true);
      const params = {
        offset: this.offset,
        limit: this.itemsPerPage,
      };
      if (this.search) {
        params.search = this.search;
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.movement_date_from) {
        params.movement_date_from = this.filters.movement_date_from;
      }
      if (this.filters.movement_date_to) {
        params.movement_date_to = this.filters.movement_date_to;
      }
      this.$api.draftSubHire
        .getDraftSubHireList(params)
        .then((res) => {
          this.allList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.getList();
      }
    }
  },
};
</script>
